"use client";

import type { ReactNode } from "react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
  ChainOfThoughtPreview,
  type ChainOfThoughtHeaderStatus,
} from "../ai-elements/chain-of-thought";
import { STEP_DETAIL_INDENT } from "./ToolComponents";

export function ChainEntry({
  headerText,
  status,
  preview,
  isProcessing,
  waitingFor,
  isLastInTurn,
  workedMs,
  children,
}: {
  headerText: string;
  status: ChainOfThoughtHeaderStatus;
  preview: string | null;
  isProcessing: boolean;
  waitingFor: "approval" | "answer" | null;
  isLastInTurn: boolean;
  workedMs?: number;
  children: ReactNode;
}) {
  const t = useTranslations("widget.chain");
  const worked = workedMs ?? null;

  const isRunning = isProcessing && isLastInTurn;
  const waiting = isLastInTurn ? waitingFor : null;
  const reportsDuration = isLastInTurn && !isProcessing && status !== "error";

  return (
    <ChainOfThought defaultOpen={false} className="space-y-0 max-w-none">
      <ChainOfThoughtHeader
        status={waiting ? "waiting" : isRunning ? "running" : status}
      >
        {waiting === "approval"
          ? t("waitingForApproval")
          : waiting === "answer"
            ? t("waitingForAnswer")
            : reportsDuration
              ? worked === null
                ? t("workedForAFewSeconds")
                : t("workedForSeconds", {
                    seconds: Math.max(1, Math.round(worked / 1000)),
                  })
              : headerText}
      </ChainOfThoughtHeader>
      {isRunning && preview && (
        <ChainOfThoughtPreview
          className={cn("mt-1.5 line-clamp-3", STEP_DETAIL_INDENT)}
        >
          {preview}
        </ChainOfThoughtPreview>
      )}
      <ChainOfThoughtContent>{children}</ChainOfThoughtContent>
    </ChainOfThought>
  );
}
